import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { apiErrorMessage } from "../../services/apiError";
import { withoutBidiControls as plain } from "../snippets/bidi";
import { companyPath, formatInstant } from "./lib/format";
import { formatHundredths, formatHundredthsPoints, positionRows } from "./lib/fund";
import { formatPct, formatShares, formatUsd } from "./lib/money";

/** A row's weights in words: now, the target's, the points between. */
function weightsText({ state, now, target, drift }) {
  const nowText = now === null ? "weight unknown" : `${formatHundredths(now)} now`;
  if (state === "no_target") return nowText;
  if (state === "off_target") return `${nowText} · not in the target — a sync sells it`;
  const parts = [nowText, `target ${formatPct(target)}`];
  if (drift !== null) parts.push(formatHundredthsPoints(drift));
  return `${state === "not_held" ? "Not held yet · " : ""}${parts.join(" · ")}`;
}

function Retry({ children, onRetry }) {
  return (
    <Alert variant="destructive" role="alert" className="mt-2">
      <AlertDescription className="flex flex-wrap items-center gap-2">
        <span>{children}</span>{" "}
        <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" onClick={onRetry}>
          Retry
        </Button>
      </AlertDescription>
    </Alert>
  );
}

Retry.propTypes = {
  children: PropTypes.node.isRequired,
  onRetry: PropTypes.func.isRequired,
};

/**
 * Positions as of the latest snapshot beside the approved target (spec
 * §5.5, walkthrough 11): each position's value, shares and price, its
 * weight now (value ÷ the account's total, worked out in lib/fund.js) and
 * its target weight, the points between; a target holding the account
 * doesn't hold yet; a position the target doesn't hold (a sync sells it);
 * cash beside the target's cash; the total. Names are agent-written:
 * plain text without bidi controls (D8).
 *
 * `latest` is useLatestSnapshot's state; `target` is useRead's for GET
 * target/ (its `current` is the approved version, null before one).
 */
function FundPositions({ latest, target }) {
  const { snapshot } = latest;
  const current = target.data?.current ?? null;
  let body;
  if (latest.error) {
    body = (
      <Retry onRetry={latest.retry}>
        Couldn&apos;t read the latest snapshot: {apiErrorMessage(latest.error, "it didn't load.")}
      </Retry>
    );
  } else if (latest.loading && !snapshot) {
    body = (
      <p role="status" className="m-0 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        Reading the latest snapshot…
      </p>
    );
  } else if (!snapshot) {
    body = (
      <p className="m-0 text-sm">
        No snapshot yet. Positions and cash show here once your executor records the account&apos;s first reading, at
        its first sync. Until then,{" "}
        <Link
          to="/investing/target"
          className="-mx-1 -my-3 inline-block px-1 py-3 underline underline-offset-2 md:m-0 md:p-0"
        >
          Target
        </Link>{" "}
        shows what the account should hold.
      </p>
    );
  } else {
    const { rows, cash } = positionRows(snapshot, current);
    body = (
      <>
        <p className="m-0 text-sm text-muted-foreground">
          As of {formatInstant(snapshot.as_of)} — snapshot #{snapshot.id}.
        </p>
        {target.error && (
          <Retry onRetry={target.retry}>
            Couldn&apos;t load the target, so there are no target weights:{" "}
            {apiErrorMessage(target.error, "it didn't load.")}
          </Retry>
        )}
        {target.data && !current && (
          <p className="mt-2 mb-0 text-sm">
            No target is approved yet, so there&apos;s nothing to compare with: these are the account&apos;s positions
            as read.
          </p>
        )}
        <ul aria-label="Positions and cash" className="mt-2 mb-0 flex list-none flex-col p-0">
          {rows.map((row) => (
            <li key={row.symbol} data-testid={`position-${row.symbol}`} className="border-b border-border py-2">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <Link
                  to={companyPath(row.symbol)}
                  className="inline-flex min-h-11 min-w-11 items-center font-semibold underline-offset-2 hover:underline md:min-h-0 md:min-w-0"
                >
                  {row.symbol}
                </Link>{" "}
                <span className="min-w-0 flex-1 text-muted-foreground wrap-anywhere">
                  {row.name ? plain(row.name) : ""}
                </span>{" "}
                {row.value !== null && <span className="font-semibold tabular-nums">{formatUsd(row.value)}</span>}
              </div>
              <p className="m-0 text-sm tabular-nums">{weightsText(row)}</p>
              {row.quantity !== null && (
                <p className="m-0 text-xs text-muted-foreground tabular-nums">
                  {formatShares(row.quantity)} shares at {formatUsd(row.price) ?? "an unknown price"}
                </p>
              )}
            </li>
          ))}
          <li data-testid="position-cash" className="border-b border-border py-2">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="flex-1 font-semibold">Cash</span>{" "}
              <span className="font-semibold tabular-nums">{formatUsd(cash.value)}</span>
            </div>
            <p className="m-0 text-sm tabular-nums">
              {weightsText({ ...cash, state: current ? "held" : "no_target" })}
            </p>
          </li>
          <li data-testid="position-total" className="flex flex-wrap items-baseline gap-x-2 py-2">
            <span className="flex-1 font-semibold">Total</span>{" "}
            <span className="font-semibold tabular-nums">{formatUsd(snapshot.total_value)}</span>
          </li>
        </ul>
        <p className="mt-1 mb-0 text-xs text-muted-foreground">
          The total is cash plus positions, as Robinhood reported them. Weight now is each value ÷ that total, worked
          out here and rounded half up to 0.01%
          {current ? `; target weights are the approved target, v${current.number}` : ""}. A sync trades only what sits
          further from its target than the drift band.
        </p>
      </>
    );
  }
  return (
    <section aria-label="Positions" className="rounded-lg border bg-card p-4">
      <h2 className="mt-0 mb-1 text-lg font-semibold">Positions</h2>
      {body}
    </section>
  );
}

FundPositions.propTypes = {
  latest: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    snapshot: PropTypes.shape({
      id: PropTypes.number,
      as_of: PropTypes.string,
      cash: PropTypes.string,
      total_value: PropTypes.string,
      positions: PropTypes.arrayOf(PropTypes.object),
    }),
    error: PropTypes.object,
    retry: PropTypes.func.isRequired,
  }).isRequired,
  target: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    /** GET target/: `current` is the approved version, null before one. */
    data: PropTypes.shape({ current: PropTypes.object }),
    error: PropTypes.object,
    retry: PropTypes.func.isRequired,
  }).isRequired,
};

export default FundPositions;
